import type { Figure } from "@howcomet/shared";
import type { FigureProps } from "./registry";

const BANDS = [
  { fill: "var(--color-cyan)", text: "var(--color-night)" },
  { fill: "var(--color-lilac)", text: "var(--color-night)" },
  { fill: "var(--color-pink)", text: "var(--color-night)" },
  { fill: "var(--color-amber)", text: "var(--color-night)" },
  { fill: "var(--color-gold)", text: "var(--color-night)" },
];

/**
 * Labelled layers, stacked top to bottom. A cross-section without the artwork.
 *
 * Bands widen as they go down, which is doing real work: the layers this draws
 * — crust to core, atmosphere, soil — are almost always thin on the outside and
 * enormous underneath, and a child reading equal stripes takes away that
 * they're equal. It is the one bit of proportion the shape can afford to carry
 * honestly without claiming to be to scale, which the caption never says.
 */
export function PartsFigure({ figure }: FigureProps<Figure>) {
  const labels = figure.labels ?? [];
  if (labels.length < 2) return null;

  return (
    <div
      className="flex flex-col items-center gap-[3px]"
      role="img"
      aria-label={`Layers from the outside in: ${labels.join(", then ")}.`}
    >
      {labels.map((label, i) => {
        const width = 52 + (i / Math.max(labels.length - 1, 1)) * 48;
        const band = BANDS[i % BANDS.length]!;
        return (
          <div
            key={label}
            className="band-in disp flex items-center justify-center rounded-lg px-3 text-[15px] leading-tight"
            style={{
              width: `${width}%`,
              minHeight: 42,
              background: band.fill,
              color: band.text,
              animationDelay: `${i * 0.1}s`,
            }}
          >
            {label}
          </div>
        );
      })}
    </div>
  );
}
